import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FACEBOOK_RTMPS_URL } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { fmtTime } from '../lib/format';
import type { DestinationsData } from '../lib/streaming';
import { Button, Card, CardHeader, Empty, ErrorText, Field, Input, PageHeader, Pill, Segmented } from '../components/ui';

/**
 * Administration → Streaming (INT-STR-01): the organisation's destinations. YouTube (@k5trading) is connected by Google
 * sign-in; Facebook Pages and other platforms by server URL + stream key. Keys and tokens are stored encrypted and never
 * shown again after saving — only their last 4 characters.
 */
export function Streaming() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { data, error } = useQuery({ queryKey: ['destinations'], queryFn: () => api<DestinationsData>('/destinations') });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['destinations'] });
  const [kind, setKind] = useState<'FACEBOOK' | 'RTMP'>('FACEBOOK');
  const [name, setName] = useState('');
  const [url, setUrl] = useState(FACEBOOK_RTMPS_URL);
  const [key, setKey] = useState('');
  const add = useMutation({
    mutationFn: () => api('/destinations/rtmp', { body: { kind, name, url, streamKey: key } }),
    onSuccess: () => {
      setName('');
      setKey('');
      refresh();
    },
  });
  const remove = useMutation({ mutationFn: (id: string) => api(`/destinations/${id}`, { method: 'DELETE' }), onSuccess: refresh });
  const connect = useMutation({
    mutationFn: () => api<{ url: string }>('/destinations/youtube/connect', { body: {} }),
    onSuccess: (r) => window.location.assign(r.url),
  });
  if (error) return <Card className="p-4">{errorText(error)}</Card>;
  if (!data) return <Card className="p-4 text-sub">Loading…</Card>;
  const yt = data.destinations.filter((d) => d.kind === 'YOUTUBE');
  const others = data.destinations.filter((d) => d.kind !== 'YOUTUBE');
  const ytResult = params.get('youtube');
  return (
    <>
      <PageHeader title="Streaming" sub="Where webinars are broadcast besides the floor · keys are stored encrypted and never shown in full again" />
      {ytResult === 'connected' && <p role="status" className="m-0 rounded-lg bg-up-bg px-3 py-2 text-sm text-up">YouTube channel connected.</p>}
      {ytResult === 'error' && <p role="alert" className="m-0 rounded-lg bg-down-bg px-3 py-2 text-sm text-down">{params.get('message') ?? 'The YouTube connection failed.'}</p>}
      {(ytResult === 'connected' || ytResult === 'error') && <div><Button variant="ghost" onClick={() => setParams({})}>Dismiss</Button></div>}
      {!data.egress.configured && <p role="status" className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn">{data.egress.message}</p>}

      <Card>
        <CardHeader title="YouTube" />
        <div className="flex flex-col gap-3 p-4">
          {!data.youtube.configured ? (
            <p role="status" className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn" data-testid="youtube-not-set-up">{data.youtube.message}</p>
          ) : (
            <p className="m-0 text-sm text-sub">Sign in with the Google account that owns the channel. VTF creates each live broadcast on the channel and keeps a refresh token (encrypted) so it can do so without asking again.</p>
          )}
          {yt.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-raised px-3 py-2.5">
              <span className="flex flex-wrap items-center gap-2">
                <strong>{d.channel?.title ?? d.name}</strong>
                {d.channel?.handle && <span className="text-sub">{d.channel.handle}</span>}
                <Pill tone="up">Connected</Pill>
                <span className="text-xs text-muted">since {fmtTime(d.createdAt, tz, true)}</span>
              </span>
              {data.canManage && <Button variant="danger" onClick={() => window.confirm(`Disconnect ${d.name}? Its access token is revoked.`) && remove.mutate(d.id)}>Disconnect</Button>}
            </div>
          ))}
          {data.canManage && data.youtube.configured && (
            <div className="flex flex-col gap-1">
              <div><Button variant="primary" onClick={() => connect.mutate()} disabled={connect.isPending}>{yt.length ? 'Reconnect or add a channel' : 'Connect YouTube'}</Button></div>
              <span className="text-xs break-all text-muted">Redirect URI registered with Google: {data.youtube.redirectUri}</span>
            </div>
          )}
          <ErrorText error={connect.error} />
        </div>
      </Card>

      <Card>
        <CardHeader title="Facebook and other RTMP destinations" />
        <ul className="m-0 flex list-none flex-col p-0">
          {!others.length && <li><Empty>No RTMP destinations yet.</Empty></li>}
          {others.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3 last:border-b-0" data-testid="destination-row">
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-2">
                  <strong>{d.name}</strong>
                  <Pill>{d.kindLabel}</Pill>
                </span>
                <span className="text-xs break-all text-sub">{d.url}</span>
                <span className="text-xs text-sub">
                  Stream key <code data-testid="masked-key">{d.streamKey}</code>
                  {d.createdBy ? ` · added by ${d.createdBy.name}` : ''}
                </span>
              </span>
              {data.canManage && <Button variant="danger" aria-label={`Remove ${d.name}`} onClick={() => window.confirm(`Remove ${d.name}? Its stream key is deleted.`) && remove.mutate(d.id)}>Remove</Button>}
            </li>
          ))}
        </ul>
        <ErrorText error={remove.error} />
        {data.canManage && (
          <form
            className="flex flex-col gap-3 border-t border-line p-4"
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate();
            }}
          >
            <h2 className="m-0 text-[15px] font-semibold">Add a destination</h2>
            <Segmented
              label="Destination type"
              options={[{ value: 'FACEBOOK', label: 'Facebook Page' }, { value: 'RTMP', label: 'Custom RTMP' }]}
              value={kind}
              onChange={(k) => {
                setKind(k);
                if (k === 'FACEBOOK') setUrl(FACEBOOK_RTMPS_URL);
                else if (url === FACEBOOK_RTMPS_URL) setUrl('');
              }}
            />
            <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === 'FACEBOOK' ? 'K5 Trading Page' : 'e.g. Twitch'} maxLength={80} required /></Field>
            <Field label="Server URL" hint={kind === 'FACEBOOK' ? 'Facebook’s persistent-stream-key server; it only accepts rtmps://.' : 'rtmp:// or rtmps://'}><Input value={url} onChange={(e) => setUrl(e.target.value)} required /></Field>
            <Field label="Stream key" hint="Stored encrypted. After saving only the last 4 characters are shown."><Input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} required /></Field>
            <div><Button variant="primary" type="submit" disabled={add.isPending || !name.trim() || !key.trim()}>Save destination</Button></div>
            <ErrorText error={add.error} />
          </form>
        )}
      </Card>
    </>
  );
}
